import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MdOutlineModeEditOutline } from 'react-icons/md';
import {
  BsRecordCircle as CheckIcon,
  BsCircle as CircleIcon,
} from 'react-icons/bs';
import Button from 'partial/components/Button';
import { RadioGroup } from '@headlessui/react';
import { aclHelper } from 'helper';
import { useShowPlan } from '../hooks';

const descriptionClassName =
  'font-[500] flex-grow text-sm tracking-wide text-black';
const containerClassName = 'flex justify-between';
const labelClassName = 'w-[200px] text-sm tracking-wide';

const options = [
  {
    label: 'Yes',
    value: 1,
  },
  {
    label: 'No',
    value: 0,
  },
];

function PlanDetails() {
  const params = useParams();
  const { planId } = params;
  const navigate = useNavigate();
  const handleClickEdit = () => {
    navigate(`/settings/plans/${planId}/update-plan`);
  };

  const renderRadio = (val) => (
    <RadioGroup value={val}>
      <div className="flex gap-4 pointer-events-none">
        {options.map(({ label, value }) => (
          <RadioGroup.Option key={label} value={value}>
            {({ checked }) => (
              <div className="flex w-full items-center gap-2">
                <div className="shrink-0 text-primary">
                  {checked ? (
                    <CheckIcon className="h-4 w-4" />
                  ) : (
                    <CircleIcon className="h-4 w-4" />
                  )}
                </div>
                <div className="flex items-center">
                  <div className="text-sm">
                    <RadioGroup.Label as="p" className={descriptionClassName}>
                      {label}
                    </RadioGroup.Label>
                  </div>
                </div>
              </div>
            )}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  );

  const display = [
    {
      label: 'Name',
      key: 'name',
    },
    {
      label: 'Rate per SMS',
      key: 'rate_per_sms',
    },
    {
      label: 'Sender Name',
      key: (item) =>
        item?.mask_name_limit === '0' ? 'Unlimited' : item?.mask_name_limit,
    },
    {
      label: 'Purchase Limit',
      key: (item) =>
        item?.max_purchase_limit === '0'
          ? 'Unlimited'
          : item?.max_purchase_limit,
    },
    {
      label: 'Credit Limit',
      key: (item) =>
        item?.credit_limit === '0' ? 'Unlimited' : item?.credit_limit,
    },
    {
      label: 'Description',
      key: 'description',
    },
    {
      label: 'Upgrade Approval',
      key: (item) => renderRadio(item?.is_need_approval),
    },
    {
      label: 'Default Plan',
      key: (item) => renderRadio(item?.is_default),
    },
  ];

  const [isLoading, plan] = useShowPlan(planId);

  if (isLoading)
    return (
      <div className="grid grid-cols-2 gap-3 p-3 w-full h-fit">
        <div className="col-span-2 animate-pulse bg-gray-200 h-8 w-full rounded" />
        <div className="col-span-2 animate-pulse bg-gray-200 h-8 w-full rounded" />
        <div className="col-span-2 animate-pulse bg-gray-200 h-8 w-full rounded" />
        <div className="col-span-2 animate-pulse bg-gray-200 h-8 w-full rounded" />
        <div className="col-span-2 animate-pulse bg-gray-200 h-8 w-full rounded" />
      </div>
    );

  return (
    <div className="flex-grow pl-4">
      <header className="flex items-center justify-between pl-8 pr-4 py-[6px] rounded-sm bg-primary ">
        {plan && (
          <h2 className="text-lg font-semibold text-white tracking-wider">
            {plan?.name} PLAN
          </h2>
        )}
        <div>
          {aclHelper(
            'subscription',
            'u',
            <Button
              onClick={handleClickEdit}
              icon={MdOutlineModeEditOutline}
              size="xxs"
              link
            >
              <span>Edit</span>
            </Button>,
          )}
        </div>
      </header>
      <div className="p-5">
        <div className="space-y-5">
          {display?.map(({ label, key }) => (
            <div className="space-y-3" key={label}>
              <div className={containerClassName}>
                <h6 className={labelClassName}>{label}:</h6>
                <p className={descriptionClassName}>
                  {typeof key === 'function' ? key(plan) : plan?.[key]}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default React.memo(PlanDetails);
